import React from 'react';
import { useNavigate } from 'react-router-dom';

const DEMO_MAILTO = 'mailto:tommy@rekon.live?subject=REKON%20Demo%20Request&body=Shop%20name%3A%0AShop%20location%3A%0ANumber%20of%20bays%2Ftechs%3A%0ACurrent%20system%20(if%20any)%3A%0A';

export default function MarketingHome() {
  const navigate = useNavigate();

  return (
    <div className="mktg">
      <style>{CSS}</style>

      {/* ── NAV ──────────────────────────────────────────────────────────────── */}
      <nav className="mktg-nav">
        <div className="mktg-nav-logo">REK<span>ON</span></div>
        <div className="mktg-nav-actions">
          <a href={DEMO_MAILTO} className="mktg-nav-demo">Request Demo</a>
          <button className="mktg-nav-billing" onClick={() => navigate('/billing')}>
            Billing / Buy
          </button>
          <button className="mktg-nav-login" onClick={() => navigate('/flow')}>
            Log In
          </button>
        </div>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <div className="mktg-hero">
        <div className="mktg-hero-tag">Real-Time Operations Platform</div>
        <h1 className="mktg-h1">
          Walk into your shop and know <em>exactly</em> what is happening.
        </h1>
        <p className="mktg-hero-sub">
          Every idle truck is money sitting still. REKON turns every active job, technician,
          and repair order into one live operating picture — on the wall, on your desk, and
          in your pocket — so nothing stalls without you knowing.
        </p>
        <div className="mktg-hero-actions">
          <a href={DEMO_MAILTO} className="mktg-btn-primary">Request a Demo</a>
          <a href="#how-it-works" className="mktg-btn-secondary">See How It Works</a>
        </div>
      </div>

      {/* ── PROBLEM BAR ──────────────────────────────────────────────────────── */}
      <div className="mktg-problem-bar">
        <p>
          <strong>Most shops run on whiteboards, phone calls, and memory.</strong> When a
          job stalls, no one knows why. When a promise to a customer slips, no one saw it
          coming — until the truck's been parked for two days. REKON catches it the moment
          it happens.
        </p>
      </div>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────────────── */}
      <section id="how-it-works" className="mktg-section">
        <div className="mktg-section-label">How It Works</div>
        <div className="mktg-section-title">Three layers. One live system.</div>
        <p className="mktg-section-sub">
          REKON is built around the way shops actually operate — not the way software
          companies think they do.
        </p>
        <div className="mktg-layers">
          {[
            { tag: 'The Wall', n: '1', h: 'See the shop from across the room', p: 'The Flow board runs on any screen. Every active job, every stage, every tech assignment, every aging alert — visible from 20 feet away without touching a keyboard.' },
            { tag: 'The RO',   n: '2', h: 'Work every job from one place',      p: 'Each repair order holds the full story — complaint, findings, repairs, parts, estimates, and history. Techs access it on their phone by scanning a QR code on the printed work order.' },
            { tag: 'The Forms',n: '3', h: 'Fast entry, then back to work',      p: 'New ROs, findings, parts, and estimates are captured in focused, fast forms. Fill it in and return to the board. The form is a tool — not the destination.' },
          ].map(c => (
            <div key={c.n} className="mktg-layer-card">
              <span className="mktg-layer-tag">{c.tag}</span>
              <div className="mktg-layer-num">{c.n}</div>
              <h3>{c.h}</h3>
              <p>{c.p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────────────────────────────────────── */}
      <section className="mktg-section mktg-section-alt">
        <div className="mktg-section-label">Capabilities</div>
        <div className="mktg-section-title">Everything a shop needs to run tight.</div>
        <p className="mktg-section-sub">
          Built from real experience on the shop floor — not a spreadsheet of feature requests.
        </p>
        <div className="mktg-features-grid">
          {FEATURES.map(f => (
            <div key={f.title} className="mktg-feature">
              <div className="mktg-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#CEA046" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                  <path d={f.icon} />
                </svg>
              </div>
              <h4>{f.title}</h4>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── MONITOR STRATEGY ─────────────────────────────────────────────────── */}
      <section className="mktg-section">
        <div className="mktg-section-label">Monitor Strategy</div>
        <div className="mktg-section-title">Every screen shows the right view.</div>
        <p className="mktg-section-sub">
          REKON isn't one app on one screen. It's the same live data — formatted for
          whoever is looking at it.
        </p>
        <div className="mktg-monitors-grid">
          {[
            { loc: 'Office TV',           title: 'Flow Board',    desc: 'Every active job. Always on. Readable from across the room.' },
            { loc: 'Shop Floor TV',       title: 'Tech Board',    desc: 'Who\'s working what. No guessing. No asking around.' },
            { loc: 'Manager Desk',        title: 'Command',       desc: 'Health, blockers, aging risk, and dispatch — all in one view.' },
            { loc: 'Tech Phone / Tablet', title: 'RO Page',       desc: 'Scan the QR code. Open the job. Add notes and photos. Done.' },
          ].map(m => (
            <div key={m.loc} className="mktg-monitor">
              <div className="mktg-monitor-loc">{m.loc}</div>
              <h4>{m.title}</h4>
              <p>{m.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── PACKAGES ─────────────────────────────────────────────────────────── */}
      <section className="mktg-section mktg-section-alt">
        <div className="mktg-section-label">Packages</div>
        <div className="mktg-section-title">Built for every shop.</div>
        <p className="mktg-section-sub">Start with what you need. Add more as you grow.</p>
        <div className="mktg-packages-grid">
          <div className="mktg-pkg-card">
            <div className="mktg-pkg-name">Starter</div>
            <h3>REKON Flow</h3>
            <p>The wall display. Live job visibility without replacing your existing system.</p>
            <ul className="mktg-pkg-features">
              {['Flow board — wall display ready', 'Tech Board — second monitor', 'Today dashboard', 'Mobile-friendly'].map(f => <li key={f}>{f}</li>)}
            </ul>
            <button className="mktg-pkg-btn" onClick={() => navigate('/billing')}>Get Started →</button>
          </div>
          <div className="mktg-pkg-card mktg-pkg-featured">
            <div className="mktg-pkg-badge">Most Popular</div>
            <div className="mktg-pkg-name">Operations</div>
            <h3>REKON Ops</h3>
            <p>Full shop management. ROs, techs, parts, blockers, history, and command.</p>
            <ul className="mktg-pkg-features">
              {['Everything in Flow', 'Full RO management + QR access', 'Parts tracking per job', 'Blocker tracking', 'Command center', 'History and service records', 'Customer records'].map(f => <li key={f}>{f}</li>)}
            </ul>
            <button className="mktg-pkg-btn mktg-pkg-btn-featured" onClick={() => navigate('/billing')}>Get Started →</button>
          </div>
          <div className="mktg-pkg-card">
            <div className="mktg-pkg-name">Complete</div>
            <h3>REKON Full</h3>
            <p>From intake to invoice. Estimates, approvals, invoicing, road service, and QuickBooks export.</p>
            <ul className="mktg-pkg-features">
              {['Everything in Ops', 'Service catalog + menu pricing', 'Estimates with 3 C\'s', 'Customer text approval', 'Invoicing — email + print', 'Road service dispatch', 'QuickBooks export'].map(f => <li key={f}>{f}</li>)}
            </ul>
            <button className="mktg-pkg-btn" onClick={() => navigate('/billing')}>Get Started →</button>
          </div>
        </div>
      </section>

      {/* ── BOTTOM CTA ───────────────────────────────────────────────────────── */}
      <div className="mktg-cta">
        <h2>Ready to see your shop <em>live</em>?</h2>
        <p>REKON is running in truck repair shops now. Request a demo and see your operation in real time.</p>
        <div className="mktg-cta-actions">
          <a href={DEMO_MAILTO} className="mktg-btn-primary">Request a Demo</a>
          <button className="mktg-btn-secondary" onClick={() => navigate('/billing')}>See Pricing</button>
        </div>
      </div>

      {/* ── FOOTER ───────────────────────────────────────────────────────────── */}
      <footer className="mktg-footer">
        <div className="mktg-footer-logo">REK<span>ON</span></div>
        <div>Real-time operations for truck repair.</div>
        <div className="mktg-footer-links">
          <button className="mktg-footer-link" onClick={() => navigate('/privacy')}>Privacy Policy</button>
          <a href={DEMO_MAILTO}>tommy@rekon.live</a>
        </div>
      </footer>
    </div>
  );
}

// ── Data ──────────────────────────────────────────────────────────────────────

const FEATURES = [
  { title: 'Flow Board',              icon: 'M3 3h18v18H3zM3 9h18M9 3v18',             desc: 'Live Kanban view of every active job. Wall-display ready. Drag to move stages.' },
  { title: 'RO Management',           icon: 'M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11', desc: 'Full repair orders with 3 C\'s documentation, activity stream, photos, and QR code access.' },
  { title: 'Blocker Tracking',        icon: 'M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zM12 6v6l4 2', desc: 'Flag exactly why a job isn\'t moving — parts, authorization, customer, vendor.' },
  { title: 'Estimates & Approval',    icon: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M16 13H8M16 17H8', desc: 'Build estimates from your service catalog. Send for customer approval by text or email link.' },
  { title: 'Tech Board',              icon: 'M2 3h20v14H2zM8 21h8M12 17v4',            desc: 'A second-monitor view showing every tech, their assigned jobs, and current status.' },
  { title: 'Road Service Dispatch',   icon: 'M10 17h4V5H6l-2 6v6h2M14 17h2V9h3l2 4v4h-2M8 19a2 2 0 100-4 2 2 0 000 4zM18 19a2 2 0 100-4 2 2 0 000 4', desc: 'Manage roadside calls, assign techs, track locations on a map.' },
  { title: 'Invoicing',               icon: 'M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6', desc: 'Convert approved estimates to invoices. Email, print, and export to QuickBooks.' },
  { title: 'Command Center',          icon: 'M22 12h-4l-3 9L9 3l-3 9H2',              desc: 'KPIs, pipeline health, aging jobs, blocker breakdown, and tech load — plus a live AI read of your shop.' },
];

// ── Styles ────────────────────────────────────────────────────────────────────

const CSS = `
  .mktg { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1F2937; background: #fff; line-height: 1.6; }
  .mktg *, .mktg *::before, .mktg *::after { box-sizing: border-box; }
  .mktg a { text-decoration: none; color: inherit; }

  /* NAV */
  .mktg-nav { background: #0B2265; padding: 0 5%; display: flex; align-items: center; justify-content: space-between; height: 64px; position: sticky; top: 0; z-index: 100; }
  .mktg-nav-logo { font-size: 22px; font-weight: 800; letter-spacing: 0.12em; color: #fff; }
  .mktg-nav-logo span { color: #CEA046; }
  .mktg-nav-actions { display: flex; align-items: center; gap: 10px; }
  .mktg-nav-demo { background: transparent; color: rgba(255,255,255,0.75); font-size: 13px; font-weight: 600; padding: 8px 14px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.25); cursor: pointer; transition: border-color 0.15s; }
  .mktg-nav-demo:hover { border-color: rgba(255,255,255,0.6); color: #fff; }
  .mktg-nav-billing { background: #CEA046; color: #0B2265; font-weight: 700; font-size: 14px; padding: 9px 18px; border-radius: 6px; border: none; cursor: pointer; transition: background 0.15s; }
  .mktg-nav-billing:hover { background: #e8c47a; }
  .mktg-nav-login { background: transparent; color: #fff; font-weight: 600; font-size: 14px; padding: 9px 18px; border-radius: 6px; border: 1.5px solid rgba(255,255,255,0.4); cursor: pointer; transition: border-color 0.15s; }
  .mktg-nav-login:hover { border-color: rgba(255,255,255,0.8); }

  /* HERO */
  .mktg-hero { background: #0B2265; color: #fff; padding: 100px 5% 90px; text-align: center; }
  .mktg-hero-tag { display: inline-block; background: rgba(206,160,70,0.15); border: 1px solid rgba(206,160,70,0.4); color: #CEA046; font-size: 12px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; padding: 6px 16px; border-radius: 100px; margin-bottom: 28px; }
  .mktg-h1 { font-size: clamp(32px,5vw,56px); font-weight: 800; line-height: 1.15; letter-spacing: -0.02em; max-width: 820px; margin: 0 auto 24px; }
  .mktg-h1 em { font-style: normal; color: #CEA046; }
  .mktg-hero-sub { font-size: clamp(16px,2vw,20px); color: rgba(255,255,255,0.72); max-width: 580px; margin: 0 auto 40px; line-height: 1.7; }
  .mktg-hero-actions { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; }
  .mktg-btn-primary { background: #CEA046; color: #0B2265; font-weight: 700; font-size: 16px; padding: 14px 32px; border-radius: 8px; border: none; cursor: pointer; transition: background 0.2s; display: inline-block; }
  .mktg-btn-primary:hover { background: #e8c47a; }
  .mktg-btn-secondary { background: transparent; color: #fff; font-weight: 600; font-size: 16px; padding: 14px 32px; border-radius: 8px; border: 1.5px solid rgba(255,255,255,0.3); cursor: pointer; transition: border-color 0.2s; display: inline-block; }
  .mktg-btn-secondary:hover { border-color: rgba(255,255,255,0.7); }

  /* PROBLEM BAR */
  .mktg-problem-bar { background: #F0F4FF; border-bottom: 1px solid #E5E7EB; padding: 28px 5%; text-align: center; }
  .mktg-problem-bar p { font-size: 15px; color: #6B7280; max-width: 680px; margin: 0 auto; }
  .mktg-problem-bar strong { color: #1F2937; }

  /* SECTIONS */
  .mktg-section { padding: 80px 5%; }
  .mktg-section-alt { background: #F9FAFB; }
  .mktg-section-label { font-size: 12px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: #CEA046; margin-bottom: 12px; }
  .mktg-section-title { font-size: clamp(24px,3.5vw,38px); font-weight: 800; letter-spacing: -0.02em; color: #0B2265; line-height: 1.2; margin-bottom: 16px; max-width: 600px; }
  .mktg-section-sub { font-size: 17px; color: #6B7280; max-width: 520px; margin-bottom: 56px; line-height: 1.7; }

  /* LAYERS */
  .mktg-layers { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px,1fr)); gap: 24px; }
  .mktg-layer-card { border: 1px solid #E5E7EB; border-radius: 12px; padding: 28px; background: #fff; }
  .mktg-layer-tag { display: inline-block; font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #CEA046; background: rgba(206,160,70,0.1); padding: 3px 10px; border-radius: 100px; margin-bottom: 14px; }
  .mktg-layer-num { width: 36px; height: 36px; border-radius: 50%; background: #0B2265; color: #CEA046; font-weight: 800; font-size: 15px; display: flex; align-items: center; justify-content: center; margin-bottom: 18px; }
  .mktg-layer-card h3 { font-size: 18px; font-weight: 700; color: #0B2265; margin-bottom: 8px; }
  .mktg-layer-card p { font-size: 15px; color: #6B7280; line-height: 1.65; }

  /* FEATURES */
  .mktg-features-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px,1fr)); gap: 20px; }
  .mktg-feature { padding: 24px; background: #fff; border-radius: 10px; }
  .mktg-feature-icon { width: 40px; height: 40px; background: #0B2265; border-radius: 8px; display: flex; align-items: center; justify-content: center; margin-bottom: 14px; }
  .mktg-feature h4 { font-size: 15px; font-weight: 700; color: #0B2265; margin-bottom: 6px; }
  .mktg-feature p { font-size: 14px; color: #6B7280; line-height: 1.6; }

  /* MONITORS */
  .mktg-monitors-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px,1fr)); gap: 16px; margin-top: 48px; }
  .mktg-monitor { border-left: 3px solid #CEA046; padding: 16px 20px; background: #fff; border-radius: 0 8px 8px 0; }
  .mktg-monitor-loc { font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #CEA046; margin-bottom: 6px; }
  .mktg-monitor h4 { font-size: 16px; font-weight: 700; color: #0B2265; margin-bottom: 4px; }
  .mktg-monitor p { font-size: 13px; color: #6B7280; }

  /* PACKAGES */
  .mktg-packages-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px,1fr)); gap: 24px; }
  .mktg-pkg-card { background: #fff; border: 1px solid #E5E7EB; border-radius: 14px; padding: 32px 28px; position: relative; display: flex; flex-direction: column; }
  .mktg-pkg-featured { border: 2px solid #0B2265; }
  .mktg-pkg-badge { position: absolute; top: -13px; left: 50%; transform: translateX(-50%); background: #0B2265; color: #CEA046; font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; padding: 4px 16px; border-radius: 100px; white-space: nowrap; }
  .mktg-pkg-name { font-size: 13px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #CEA046; margin-bottom: 8px; }
  .mktg-pkg-card h3 { font-size: 22px; font-weight: 800; color: #0B2265; margin-bottom: 10px; }
  .mktg-pkg-card > p { font-size: 14px; color: #6B7280; margin-bottom: 24px; line-height: 1.6; }
  .mktg-pkg-features { list-style: none; padding: 0; margin: 0 0 28px; flex: 1; }
  .mktg-pkg-features li { font-size: 14px; color: #1F2937; padding: 6px 0 6px 22px; position: relative; border-bottom: 1px solid #E5E7EB; }
  .mktg-pkg-features li:last-child { border-bottom: none; }
  .mktg-pkg-features li::before { content: ''; position: absolute; left: 0; top: 13px; width: 8px; height: 8px; border-radius: 50%; background: #CEA046; }
  .mktg-pkg-btn { display: block; width: 100%; text-align: center; padding: 12px; border-radius: 8px; font-weight: 700; font-size: 14px; border: 1.5px solid #0B2265; color: #0B2265; background: transparent; cursor: pointer; transition: all 0.2s; }
  .mktg-pkg-btn:hover { background: #0B2265; color: #fff; }
  .mktg-pkg-btn-featured { background: #0B2265; color: #fff; }
  .mktg-pkg-btn-featured:hover { background: #0d2d80; }

  /* BOTTOM CTA */
  .mktg-cta { background: #0B2265; color: #fff; text-align: center; padding: 80px 5%; }
  .mktg-cta h2 { font-size: clamp(26px,4vw,42px); font-weight: 800; letter-spacing: -0.02em; margin-bottom: 16px; }
  .mktg-cta h2 em { font-style: normal; color: #CEA046; }
  .mktg-cta p { font-size: 17px; color: rgba(255,255,255,0.7); max-width: 480px; margin: 0 auto 36px; }
  .mktg-cta-actions { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; }

  /* FOOTER */
  .mktg-footer { background: #060f2e; color: rgba(255,255,255,0.5); padding: 32px 5%; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; font-size: 13px; }
  .mktg-footer-logo { font-size: 16px; font-weight: 800; letter-spacing: 0.12em; color: #fff; }
  .mktg-footer-logo span { color: #CEA046; }
  .mktg-footer a { color: rgba(255,255,255,0.5); }
  .mktg-footer a:hover { color: #CEA046; }
  .mktg-footer-links { display: flex; align-items: center; gap: 18px; flex-wrap: wrap; justify-content: center; }
  .mktg-footer-link { background: none; border: none; padding: 0; font: inherit; color: rgba(255,255,255,0.5); cursor: pointer; }
  .mktg-footer-link:hover { color: #CEA046; }

  @media (max-width: 600px) {
    .mktg-nav { padding: 0 4%; }
    .mktg-nav-demo { display: none; }
    .mktg-hero { padding: 64px 4% 60px; }
    .mktg-section { padding: 60px 4%; }
    .mktg-footer { flex-direction: column; text-align: center; }
  }
`;
